import React, { useState, useEffect, useRef } from 'react';
import OrderCard from './components/OrderCard/OrderCard';
import Chat from './components/Chat/Chat';
import NotificationBell from './components/NotificationBell/NotificationBell';
import NotificationPermission from './components/NotificationPermission/NotificationPermission';
import ToastManager from './components/Toast/ToastManager';
import Login from './components/Login/Login';
import { AppContainer, Header, HeaderContent, MainContent, OrdersList, LogoutButton } from './App.styles';
import authService, { User } from './services/authService';
import { disconnectEcho } from './services/laravelEchoService';
import { clearAllSubscriptions } from './services/connectionManager';
import { registerFCMToken, setupForegroundMessageHandler, getNotificationPermission, registerServiceWorker } from './services/pushNotificationService';
import axios from 'axios';

// Usar variável de ambiente ou URL relativa
const API_URL = process.env.REACT_APP_API_URL || window.location.origin + '/api';

interface Order {
  id: number;
  store_id: number;
  store_name: string;
  status: string;
  total: number;
}

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentChatId, setCurrentChatId] = useState<number | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const tokenRegisteredRef = useRef<boolean>(false);
  
  // Buscar pedidos do usuário do backend
  useEffect(() => {
    if (user) {
      const fetchOrders = async () => {
        try {
          const response = await axios.get<Order[]>(`${API_URL}/orders`, {
            withCredentials: true,
          });
          setOrders(response.data);
        } catch (error) {
          console.error('Erro ao buscar pedidos:', error);
          // Em caso de erro, manter array vazio (não mostrar pedidos fake)
          setOrders([]);
        }
      };
      
      fetchOrders();
    } else {
      setOrders([]);
    }
  }, [user]);

  useEffect(() => {
    checkAuth();
    
    // CRÍTICO: NÃO inicializar Laravel Echo na home
    // Echo será inicializado apenas quando um chat for aberto
    // Isso economiza recursos do servidor
    
    // Registrar service worker quando app inicia
    if ('serviceWorker' in navigator) {
      registerServiceWorker();
    }
    
    // Cleanup quando janela fecha ou app desmonta
    const handleBeforeUnload = () => {
      console.log('App: Janela fechando, limpando subscriptions e desconectando Laravel Echo...');
      clearAllSubscriptions();
      disconnectEcho();
    };
    
    const handleVisibilityChange = () => {
      // Se a página ficou oculta por muito tempo, pode desconectar
      // Mas não desconectar imediatamente para permitir múltiplas abas
      if (document.hidden) {
        console.log('App: Página oculta');
      } else {
        console.log('App: Página visível novamente');
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Cleanup ao desmontar componente
      clearAllSubscriptions();
      disconnectEcho();
    };
  }, []);

  useEffect(() => {
    if (user && !tokenRegisteredRef.current) {
      // Verificar se já tem permissão e registrar token automaticamente
      const permission = getNotificationPermission();
      if (permission === 'granted') {
        // Se já tem permissão, registrar token automaticamente (apenas uma vez)
        registerFCMToken(user.id).then(() => {
          tokenRegisteredRef.current = true;
        });
      }
      
      // Função para processar notificação FCM e mostrar toast + notificação nativa
      // IMPORTANTE: Backend agora envia title/body em data (não em notification)
      // Quando em foreground, o App mostra notificação nativa
      // Quando em background, o service worker mostra notificação nativa
      const processFCMNotification = (payload: any, isForeground: boolean = false) => {
        
        // Backend agora envia title e body em data (não em notification)
        const title = payload.data?.title || payload.notification?.title || 'Nova mensagem';
        const body = payload.data?.body || payload.notification?.body || '';
        const data = payload.data || {};
        const conversationId = data.conversation_id ? parseInt(data.conversation_id) : undefined;
        
        // Não mostrar toast se o chat da conversa estiver aberto E a janela estiver em foco
        // Nesse caso, apenas atualizar contador de notificações silenciosamente
        const shouldShowToast = !(
          conversationId && 
          currentChatId === conversationId && 
          document.hasFocus() && 
          document.visibilityState === 'visible'
        );
        
        if (shouldShowToast) {
          // Mostrar toast
          setTimeout(() => {
            if ((window as any).showToast) {
              (window as any).showToast({
                id: `toast-${Date.now()}-${Math.random()}`,
                title: title,
                body: body,
                conversationId: conversationId,
              });
            }
          }, 100);
        } else {
          // Chat está aberto e em foco, apenas atualizar contador silenciosamente
          console.log('App: Chat está aberto e em foco, atualizando contador silenciosamente');
        }
        
        // Mostrar notificação nativa quando em foreground E janela visível MAS SEM FOCUS
        // Se janela está com foco: apenas toast (não mostrar notificação nativa)
        // Se janela está aberta mas sem foco (segunda tela): toast + notificação nativa
        // Service worker mostrará apenas quando site está fechado ou minimizado (não visível)
        if (isForeground && document.visibilityState === 'visible' && !document.hasFocus()) {
          // Verificar se tem permissão
          if (Notification.permission === 'granted') {
            const notificationTitle = `[APP-FOREGROUND] ${title}`;
            const uniqueTag = `notification-${conversationId || 'general'}-${Date.now()}`;
            
            const notification = new Notification(notificationTitle, {
              body: body,
              icon: '/icon-fm-192x192.svg',
              badge: '/icon-fm-72x72.svg',
              tag: uniqueTag,
              data: { ...data, conversationId },
            });
            
            // Ao clicar na notificação, abrir nova aba com ?chat=id
            notification.onclick = (event) => {
              event.preventDefault();
              notification.close();
              if (conversationId) {
                const url = `${window.location.origin}?chat=${conversationId}`;
                window.open(url, '_blank');
              }
            };
          }
        }
        
        // A notificação já foi registrada no backend pelo registerNotificationFromFCM
        // Disparar evento para NotificationBell recarregar e atualizar contador
        // Usar setTimeout para garantir que o backend processou e salvou no arquivo JSON
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('fcmNotificationReceived', { 
            detail: payload 
          }));
        }, 500);
      };
      
      // Configurar handler de notificações foreground (FCM) - quando a janela está em foco
      // NOTA: Quando em foreground, o FCM chama onMessage
      // O App mostra notificação nativa quando em foreground (janela em foco e visível)
      // Service worker mostra apenas quando em background (sem janela em foco)
      setupForegroundMessageHandler((payload) => {
        // Processar notificação (mostra toast + notificação nativa se em foreground)
        processFCMNotification(payload, true);
      });

      // Escutar mensagens do Service Worker
      // Quando service worker envia mensagem, pode ser:
      // 1. Background: service worker mostrou notificação, apenas mostrar toast
      // 2. Foreground: service worker detectou janela em foco, App deve mostrar notificação
      const handleServiceWorkerMessage = (event: any) => {
        if (event.data && event.data.type === 'FCM_NOTIFICATION') {
          // IMPORTANTE: Backend agora envia title/body em data (não em notification)
          const payload = event.data.payload;
          const title = payload.data?.title || payload.notification?.title || 'Nova mensagem';
          const body = payload.data?.body || payload.notification?.body || '';
          const data = payload.data || {};
          const conversationId = data.conversation_id ? parseInt(data.conversation_id) : undefined;
          
          // Não mostrar toast se o chat da conversa estiver aberto E a janela estiver em foco
          const shouldShowToast = !(
            conversationId && 
            currentChatId === conversationId && 
            document.hasFocus() && 
            document.visibilityState === 'visible'
          );
          
          if (shouldShowToast) {
            // Mostrar toast
            setTimeout(() => {
              if ((window as any).showToast) {
                (window as any).showToast({
                  id: `toast-${Date.now()}-${Math.random()}`,
                  title: title,
                  body: body,
                  conversationId: conversationId,
                });
              }
            }, 100);
          } else {
            // Chat está aberto e em foco, apenas atualizar contador silenciosamente
            console.log('App: Chat está aberto e em foco, atualizando contador silenciosamente');
          }
          
          // Se janela está visível MAS SEM FOCUS, mostrar notificação nativa
          // Se janela está com foco: apenas toast (não mostrar notificação nativa)
          // Service worker não mostrou (detectou janela aberta), então App deve mostrar se sem foco
          if (document.visibilityState === 'visible' && !document.hasFocus()) {
            if (Notification.permission === 'granted') {
              const notificationTitle = `[APP-FOREGROUND] ${title}`;
              const uniqueTag = `notification-${conversationId || 'general'}-${Date.now()}`;
              
              const notification = new Notification(notificationTitle, {
                body: body,
                icon: '/icon-fm-192x192.svg',
                badge: '/icon-fm-72x72.svg',
                tag: uniqueTag,
                data: { ...data, conversationId },
              });
              
              // Ao clicar na notificação, abrir nova aba com ?chat=id
              notification.onclick = (event) => {
                event.preventDefault();
                notification.close();
                if (conversationId) {
                  const url = `${window.location.origin}?chat=${conversationId}`;
                  window.open(url, '_blank');
                }
              };
            }
          }
          
          // Disparar evento para NotificationBell recarregar
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('fcmNotificationReceived', { 
              detail: payload 
            }));
          }, 500);
        }
      };

      // Configurar listener do service worker
      if ('serviceWorker' in navigator) {
        // Escutar mensagens do service worker quando estiver pronto
        navigator.serviceWorker.ready.then((registration) => {
          // Escutar mensagens através do service worker
          // Usar 'any' para contornar limitação de tipos do TypeScript
          (navigator.serviceWorker as any).addEventListener('message', handleServiceWorkerMessage);
        });

        // Também escutar imediatamente se o controller já estiver disponível
        if (navigator.serviceWorker.controller) {
          (navigator.serviceWorker as any).addEventListener('message', handleServiceWorkerMessage);
        }
      }

      return () => {
        // Cleanup: remover listeners quando componente desmontar
        if ('serviceWorker' in navigator) {
          (navigator.serviceWorker as any).removeEventListener('message', handleServiceWorkerMessage);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handlePermissionGranted = () => {
    // Quando permissão é concedida, o componente já registra o token
    // Marcar como registrado para evitar duplicatas
    tokenRegisteredRef.current = true;
  };

  useEffect(() => {
    // Verificar query parameter ?chat=id na inicialização
    const urlParams = new URLSearchParams(window.location.search);
    const chatId = urlParams.get('chat');
    if (chatId) {
      const conversationId = parseInt(chatId);
      if (!isNaN(conversationId)) {
        console.log('App: Chat ID encontrado no query parameter:', conversationId);
        setCurrentChatId(conversationId);
        // Remover query parameter da URL sem recarregar a página
        urlParams.delete('chat');
        const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, []);

  useEffect(() => {
    // Escutar evento de abrir chat via notificação
    const handleOpenChat = (event: CustomEvent) => {
      setCurrentChatId(event.detail.conversationId);
    };

    window.addEventListener('openChat', handleOpenChat as EventListener);
    return () => {
      window.removeEventListener('openChat', handleOpenChat as EventListener);
    };
  }, []);


  const checkAuth = async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = async () => {
    tokenRegisteredRef.current = false; // Reset ao fazer login novamente
    await checkAuth();
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      setUser(null);
      setCurrentChatId(null);
      clearAllSubscriptions();
      disconnectEcho();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  const handleChatClick = async (orderId: number, storeId?: number) => {
    if (!user) return;
    
    try {
      // Encontrar o pedido para pegar o store_id
      const order = orders.find(o => o.id === orderId);
      const orderStoreId = order?.store_id || storeId;
      
      // Criar ou buscar conversa para este pedido (backend já verifica se existe)
      const response = await axios.post(
        `${API_URL}/conversations`,
        {
          order_id: orderId,
          store_id: orderStoreId,
          type: 'order_support',
        },
        {
          withCredentials: true,
        }
      );

      // Backend retorna conversa existente ou nova
      setCurrentChatId(response.data.id);
    } catch (error) {
      const err: any = error;
      console.error('Erro ao abrir chat:', err);
      alert(err.response?.data?.error || 'Erro ao abrir chat');
    }
  };

  const handleCloseChat = () => {
    setCurrentChatId(null);
  };

  const handleToastClick = (conversationId: number) => {
    console.log('App: Toast clicado, abrindo chat diretamente:', conversationId);
    // Abrir chat diretamente sem recarregar página
    setCurrentChatId(conversationId);
  };

  const handleNotificationClick = (conversationId: number) => {
    console.log('App: Notificação clicada, abrindo chat diretamente:', conversationId);
    // Abrir chat diretamente sem recarregar página
    setCurrentChatId(conversationId);
  };

  if (loading) {
    return (
      <>
        <ToastManager onToastClick={handleToastClick} currentChatId={currentChatId} />
        <div>Carregando...</div>
      </>
    );
  }

  if (!user) {
    return (
      <>
        <ToastManager onToastClick={handleToastClick} currentChatId={currentChatId} />
        <Login onLoginSuccess={handleLoginSuccess} />
      </>
    );
  }

  return (
    <AppContainer>
      <ToastManager onToastClick={handleToastClick} currentChatId={currentChatId} />
      
      {user && (
        <NotificationPermission
          userId={user.id}
          onPermissionGranted={handlePermissionGranted}
        />
      )}
      
      <Header>
        <HeaderContent>
          <h1><span role="img" aria-label="Carrinho">🛒</span> Meus Pedidos - {user.name}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <NotificationBell userId={user.id} onNotificationClick={handleNotificationClick} />
            <LogoutButton onClick={handleLogout}>Sair</LogoutButton>
          </div>
        </HeaderContent>
      </Header>

      <MainContent>
        <OrdersList>
          {orders.map((order) => (
            <OrderCard
              key={order.id}
              orderId={order.id}
              storeId={order.store_id}
              storeName={order.store_name}
              status={order.status}
              total={order.total}
              onChatClick={() => handleChatClick(order.id, order.store_id)}
            />
          ))}
        </OrdersList>
      </MainContent>

      {currentChatId && (
        <Chat conversationId={currentChatId} onClose={handleCloseChat} />
      )}
    </AppContainer>
  );
};

export default App;
